import * as Sentry from "@sentry/react";
import { MODE, SENTRY_RELEASE } from "./config/vars";

// <-- Core imports -->
if (MODE !== "development") {
  Sentry.init({
    dsn: "https://33529618683fc941859fb045ef77b9b8@o4506944600276992.ingest.us.sentry.io/4506945083015168",
    environment: MODE,
    release: SENTRY_RELEASE,
    integrations: [Sentry.browserTracingIntegration(), Sentry.browserProfilingIntegration()],
    tracePropagationTargets: [
      /^https:\/\/api\.go-2-work\.com\//,
      /^https:\/\/api\.staging.go2work\.com\//,
      /^https:\/\/api\.go2work\.com\//,
      /^https:\/\/api\.revenuecat\.com\//,
    ],
    tracesSampleRate: 1.0,
    profilesSampleRate: 1.0,
  });
}

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import React, { useEffect } from "react";

import { createRoot } from "react-dom/client";
import { pdfjs } from "react-pdf";
import { Provider } from "react-redux";
import { BrowserRouter, useLocation } from "react-router-dom";

import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "tippy.js/dist/tippy.css";

// <-- Store imports -->
import { store } from "@/store";

// <-- Configuration Imports -->
import "./i18n/index";

// <-- Styles imports -->
import "@/styles/globals.css";

// <-- Components imports -->
import App from "./App";
import { Toaster } from "./components/ui/toaster";
import PermissionDialog from "./components/PermissionDialog";
import PosthogProvider from "./providers/PosthogProvider";

const queryClient = new QueryClient();

const root = createRoot(document.getElementById("root"));

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const CHUNK_LOAD_ERROR_REGEX =
  /Loading chunk \d+ failed|Failed to fetch dynamically imported module|Importing a module script failed/i;
const RELOAD_ON_CHUNK_FAIL_KEY = "chunk-load-reload";

const getChunkErrorMessage = (error: unknown) => {
  if (!error) return "";
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  return String(error);
};

const getChunkErrorSource = (error: unknown) => {
  if (!error || typeof error !== "object") return "";
  const maybeError = error as { filename?: string; stack?: string };
  return maybeError.filename || maybeError.stack || "";
};

const hasAssetScriptHint = (text: string) => /\/assets\/|\.js(\?|$)/i.test(text);

const isChunkLoadError = (error: unknown) => {
  if (!error) return false;
  const message = getChunkErrorMessage(error);
  const source = getChunkErrorSource(error);
  if (!CHUNK_LOAD_ERROR_REGEX.test(message)) return false;
  return hasAssetScriptHint(message) || hasAssetScriptHint(source);
};

const reloadOnceOnChunkError = () => {
  if (sessionStorage.getItem(RELOAD_ON_CHUNK_FAIL_KEY)) return;
  sessionStorage.setItem(RELOAD_ON_CHUNK_FAIL_KEY, "1");
  window.location.reload();
};

window.addEventListener("error", (event) => {
  const errorMessage = getChunkErrorMessage(event.error) || event.message;
  const errorSource = event.filename || getChunkErrorSource(event.error);
  const combined = `${errorMessage} ${errorSource}`.trim();
  if (isChunkLoadError(combined)) {
    Sentry.captureMessage("Chunk load error detected", {
      level: "warning",
      tags: { type: "chunk-load" },
      extra: {
        url: window.location.href,
        message: errorMessage,
        source: errorSource,
        userAgent: navigator.userAgent,
        reloaded: sessionStorage.getItem(RELOAD_ON_CHUNK_FAIL_KEY) ? "no" : "yes",
      },
    });
    reloadOnceOnChunkError();
  }
});

window.addEventListener("unhandledrejection", (event) => {
  const reasonMessage = getChunkErrorMessage(event.reason);
  const reasonSource = getChunkErrorSource(event.reason);
  const combined = `${reasonMessage} ${reasonSource}`.trim();
  if (isChunkLoadError(combined)) {
    Sentry.captureMessage("Chunk load error detected (unhandledrejection)", {
      level: "warning",
      tags: { type: "chunk-load" },
      extra: {
        url: window.location.href,
        message: reasonMessage,
        source: reasonSource,
        userAgent: navigator.userAgent,
        reloaded: sessionStorage.getItem(RELOAD_ON_CHUNK_FAIL_KEY) ? "no" : "yes",
      },
    });
    reloadOnceOnChunkError();
  }
});

const clearChunkReloadFlag = () => {
  sessionStorage.removeItem(RELOAD_ON_CHUNK_FAIL_KEY);
};

if (document.readyState === "complete") {
  clearChunkReloadFlag();
} else {
  window.addEventListener("load", clearChunkReloadFlag);
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    const performScroll = () => {
      // Scroll all scrollable containers to top
      const scrollContainers = document.querySelectorAll(
        ".overflow-y-auto, .overflow-auto, [data-scroll-container]",
      );

      if (scrollContainers.length > 0) {
        scrollContainers.forEach((container) => {
          if (container instanceof HTMLElement) {
            container.scrollTop = 0;
          }
        });
        return true;
      }

      // Fallback to window scroll
      window.scrollTo({ top: 0, behavior: "instant" });
      return false;
    };

    // Try immediately
    const foundContainers = performScroll();

    // If no containers were found, try again after a small delay to handle lazy-loaded content
    let timeoutId: NodeJS.Timeout | undefined;
    if (!foundContainers) {
      timeoutId = setTimeout(() => {
        performScroll();
      }, 100);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [pathname]);

  return null;
}

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <PosthogProvider>
            <ScrollToTop />
            <App />
            <Toaster />
            <PermissionDialog />
          </PosthogProvider>
        </BrowserRouter>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </Provider>
  </React.StrictMode>,
);
